import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import MENTALAutomationSection from '../pages/MENTALAutomationSection';
import MDeRCMMentalComponent from '../pages/MDeRCMMentalComponent';
import ConsultationCTA from '../components/ConsultationCTA';

// Improved Mental Health RCM Visual Component
const ImprovedMentalHealthRCMVisual = () => {
  const [activeStep, setActiveStep] = useState(0);
const [particlesVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    { 
      icon: '🧠', 
      label: 'Authorization', 
      sublabel: 'Approved',
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      position: { top: '15%', left: '10%' }
    },
    { 
      icon: '📝', 
      label: 'Documentation', 
      sublabel: 'Verified',
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      position: { top: '15%', right: '10%' }
    },
    { 
      icon: '💰', 
      label: 'Payment', 
      sublabel: 'Processed',
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      position: { bottom: '15%', left: '10%' }
    },
    { 
      icon: '📊', 
      label: 'Compliance', 
      sublabel: 'Tracked',
      color: '#f97316',
      bgColor: 'rgba(249, 115, 22, 0.1)',
      position: { bottom: '15%', right: '10%' }
    }
  ];

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '600px',
      height: '500px',
      margin: '0 auto'
    }}>
      <style>{`
        @keyframes mental-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes mental-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        @keyframes mental-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes mental-ripple {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes mental-shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes mental-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .mental-particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.8), transparent);
          border-radius: 50%;
          animation: mental-float 3s ease-in-out infinite;
        }
        .mental-brain-container {
          animation: mental-float 6s ease-in-out infinite;
        }
        .mental-step-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .mental-step-card:hover {
          transform: translateY(-8px) scale(1.05) !important;
        }
        .mental-ai-badge {
          animation: mental-pulse 2s ease-in-out infinite;
        }
        .mental-automated-badge {
          animation: mental-shimmer 3s linear infinite;
          background: linear-gradient(
            90deg,
            rgba(139, 92, 246, 0.1) 0%,
            rgba(139, 92, 246, 0.3) 50%,
            rgba(139, 92, 246, 0.1) 100%
          );
          background-size: 1000px 100%;
        }
      `}</style>

      {/* Main Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Animated Particles */}
        {particlesVisible && [...Array(20)].map((_, i) => (
          <div
            key={i}
            className="mental-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 3}s`
            }}
          />
        ))}

        {/* Inner Content */}
        <div style={{
          position: 'relative',
          margin: '20px',
          height: 'calc(100% - 40px)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: 'inset 0 2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Automated Badge */}
          <div className="mental-automated-badge" style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            zIndex: 20,
            border: '1px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.2)'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              background: '#8b5cf6',
              borderRadius: '50%',
              animation: 'mental-pulse 2s ease-in-out infinite'
            }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#8b5cf6',
              letterSpacing: '0.5px'
            }}>
              Automated
            </span>
          </div>

          {/* Central Brain Icon */}
          <div className="mental-brain-container" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}>
            {/* Rotating Ring */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '160px',
              height: '160px',
              border: '3px dashed rgba(139, 92, 246, 0.3)',
              borderRadius: '50%',
              animation: 'mental-rotate 20s linear infinite'
            }} />

            {/* Pulsing Circle */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120px',
              height: '120px',
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2), transparent)',
              borderRadius: '50%',
              animation: 'mental-pulse 3s ease-in-out infinite'
            }} />

            {/* Brain Icon */}
            <div style={{
              fontSize: '64px',
              textAlign: 'center',
              filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15))',
              position: 'relative',
              zIndex: 5
            }}>
              🧠
            </div>
          </div>

          {/* AI Processing */}
          <div className="mental-ai-badge" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, 80px)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 14px',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '20px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            zIndex: 15
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              background: '#8b5cf6',
              borderRadius: '50%'
            }} />
            <div style={{
              width: '6px',
              height: '6px',
              background: '#8b5cf6',
              borderRadius: '50%',
              animationDelay: '0.2s'
            }} />
            <div style={{
              width: '6px',
              height: '6px',
              background: '#8b5cf6',
              borderRadius: '50%',
              animationDelay: '0.4s'
            }} />
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#8b5cf6',
              marginLeft: '4px'
            }}>
              AI Processing
            </span>
          </div>

          {/* Step Cards */}
          {steps.map((step, index) => (
            <div
              key={index}
              className="mental-step-card"
              style={{
                position: 'absolute',
                ...step.position,
                padding: '16px 20px',
                background: activeStep === index 
                  ? `linear-gradient(135deg, ${step.color}15, ${step.color}25)`
                  : step.bgColor,
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: `2px solid ${activeStep === index ? step.color : 'rgba(255, 255, 255, 0.5)'}`,
                boxShadow: activeStep === index 
                  ? `0 12px 30px ${step.color}40, 0 0 0 4px ${step.color}15`
                  : '0 8px 20px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                zIndex: activeStep === index ? 15 : 10,
                minWidth: '110px',
                transform: activeStep === index ? 'scale(1.1)' : 'scale(1)'
              }}
            >
              {activeStep === index && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '100%',
                  border: `2px solid ${step.color}`,
                  borderRadius: '16px',
                  animation: 'mental-ripple 2s ease-out infinite',
                  pointerEvents: 'none'
                }} />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  fontSize: '28px',
                  filter: `drop-shadow(0 4px 8px ${step.color}40)`,
                  animation: activeStep === index ? 'mental-bounce 1s ease-in-out infinite' : 'none'
                }}>
                  {step.icon}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#1f2937',
                  textAlign: 'center'
                }}>
                  {step.label}
                </div>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: step.color,
                  textAlign: 'center',
                  padding: '2px 8px',
                  background: `${step.color}20`,
                  borderRadius: '8px'
                }}>
                  {step.sublabel}
                </div>
              </div>
            </div>
          ))}

          {/* Connection Lines */}
          <svg style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 5
          }}>
            {steps.map((step, index) => {
              const centerX = 50;
              const centerY = 50;
              const stepX = step.position.left ? 20 : 80;
              const stepY = step.position.top ? 25 : 75;
              
              return (
                <line
                  key={index}
                  x1={`${centerX}%`}
                  y1={`${centerY}%`}
                  x2={`${stepX}%`}
                  y2={`${stepY}%`}
                  stroke={activeStep === index ? step.color : 'rgba(139, 92, 246, 0.2)'}
                  strokeWidth={activeStep === index ? '3' : '2'}
                  strokeDasharray={activeStep === index ? '0' : '5,5'}
                  style={{
                    transition: 'all 0.3s ease'
                  }}
                />
              );
            })}
          </svg>

          {/* Bottom Bar */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '20px',
            background: 'linear-gradient(to top, rgba(255, 255, 255, 0.95), transparent)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                fontSize: '24px',
                animation: 'mental-pulse 2s ease-in-out infinite'
              }}>
                🧠
              </div>
              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                  Mental Health RCM
                </div>
              </div>
            </div>

            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              MDeRCM
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '20px' }}>⚡</span>
              <div>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  95% Automated
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Corner Elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          left: '-50px',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-50px',
          right: '-50px',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }} />
      </div>

      {/* RPA Badge */}
      <div style={{
        position: 'absolute',
        bottom: '-60px',
        left: '20px',
        background: 'white',
        padding: '16px 24px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 25
      }}>
        <div style={{
          fontSize: '32px',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          padding: '8px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          🤖
        </div>
        <div>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            RPA
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Smart Mental Health
          </div>
        </div>
      </div>
    </div>
  );
};

// Main MentalHealth Component
const MentalHealth = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0f9ff 100%)',
    overflow: 'hidden',
    position: 'relative',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const backgroundElementStyle1 = {
    position: 'absolute',
    top: '-10rem',
    right: '-10rem',
    width: '20rem',
    height: '20rem',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(59, 130, 246, 0.2))',
    borderRadius: '50%',
    filter: 'blur(60px)',
    animation: 'pulse 3s ease-in-out infinite'
  };

  const backgroundElementStyle2 = {
    position: 'absolute',
    bottom: '-10rem',
    left: '-10rem',
    width: '20rem',
    height: '20rem',
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(59, 130, 246, 0.2))',
    borderRadius: '50%',
    filter: 'blur(60px)',
    animation: 'pulse 3s ease-in-out infinite',
    animationDelay: '1s'
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Mental Health RCM Billing 2026 | Best Behavioral | MDeRCM</title>
        <meta 
          name="description" 
          content="Mental health RCM billing 2026. Stop losing 20-28% revenue to billing errors. Reduce denials 70% and increase collections for behavioral health." 
        />
        <meta 
          name="keywords" 
          content="mental health rcm billing, mental health revenue cycle management, behavioral health rcm, behavioral health billing services, mental health billing services 2026, best behavioral health revenue cycle management software 2026, mental health rcm services, best rcm for mental health, behavioral health rcm companies, mental health rcm outsourcing cost, behavioral health rcm outsourcing, substance abuse billing, SUD billing services, mental health billing outsourcing, psychiatric billing services, therapy billing services, LCSW billing services, telehealth mental health billing 2026, 42 CFR Part 2 billing, MHPAEA parity billing, prior authorization mental health, mental health denial management, best mental health rcm companies 2026, top rcm companies for mental health behavioral health, key features mental health practices look for in revenue cycle management software, mental health revenue cycle management pricing plans comparison" 
        />
        <link rel="canonical" href="https://mdercm.com/MentalHealth" />
        <meta property="og:title" content="Mental Health RCM Billing 2026 | Best Behavioral | MDeRCM" />
        <meta property="og:description" content="Reduce behavioral health claim denials 70%, cut Days in A/R from 55 to 18 days. Specialized mental health & substance abuse RCM billing. HIPAA & 42 CFR Part 2 compliant. Free trial →" />
        <meta property="og:url" content="https://mdercm.com/MentalHealth" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mdercm.com/assets/mental-health-rcm-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mental Health RCM Billing 2026 | Behavioral Health Revenue Cycle Management" />
        <meta name="twitter:description" content="Best mental health RCM billing 2026 — reduce denials 70%, increase collections 40-55%. Free trial →" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.1); }
        }
        @media (max-width: 768px) {
          .main-content-mental {
            grid-template-columns: 1fr !important;
            padding: 2rem 1rem !important;
            gap: 2rem !important;
          }
        }
      `}</style>

      <div style={containerStyle}>
        <div style={backgroundElementStyle1}></div>
        <div style={backgroundElementStyle2}></div>

        {/* HERO SECTION */}
        <div className="main-content-mental" style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '4rem 1.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
          gap: '4rem',
          alignItems: 'center'
        }}>
          {/* Left Content */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0)' : 'translateX(-50px)',
            transition: 'all 1s ease-out'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '600',
              borderRadius: '9999px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              width: 'fit-content'
            }}>
              ⚡ AI-Powered Mental Health RCM
            </div>

            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: '700',
              color: '#111827',
              lineHeight: '1.1',
              margin: '1rem 0'
            }}>
              Transform Your <span style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Mental Health Practice</span> with AI Revenue Cycle Management
            </h1>

            <p style={{
              fontSize: '1.125rem',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              Leading <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</Link> specializing in mental health and behavioral health practices. 
              Our <strong>healthcare revenue cycle management services</strong> automate authorization, documentation, and payments with proven <strong>healthcare cost reduction strategies</strong>.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
              textAlign: 'center',
              marginTop: '1rem'
            }}>
              <div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}>
                  95%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Automation Rate</div>
              </div>
              <div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}>
                  50%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Cost Reduction</div>
              </div>
              <div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #f97316, #dc2626)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}>
                  70%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Revenue Increase</div>
              </div>
            </div>

            <Link 
              to="/free-trial" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '9999px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                border: 'none',
                transition: 'all 0.3s ease',
                marginTop: '1rem',
                textDecoration: 'none',
                width: 'fit-content'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              Start Your Free Trial →
            </Link>
          </div>

          {/* Right Content - Improved Visual */}
          <div style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
            transition: 'all 1s ease-out 0.3s'
          }}>
            <ImprovedMentalHealthRCMVisual />
          </div>
        </div>

        {/* RELATED SERVICES SECTION - 4x2 GRID */}
        <div style={{ 
          padding: '80px 5%', 
          backgroundColor: '#f7f9fb',
          position: 'relative',
          zIndex: 10
        }}>
          <style>{`
            .mental-services-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 30px;
              max-width: 1400px;
              margin: 0 auto;
            }
            @media (max-width: 1200px) {
              .mental-services-grid {
                grid-template-columns: repeat(3, 1fr);
              }
            }
            @media (max-width: 900px) {
              .mental-services-grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }
            @media (max-width: 600px) {
              .mental-services-grid {
                grid-template-columns: 1fr;
              }
            }
          `}</style>

          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#001F2B'
          }}>
            Medical Billing Outsourcing Services - Complete Mental Health RCM Solutions
          </h2>

          <p style={{ 
            textAlign: 'center', 
            fontSize: '17px', 
            color: '#555', 
            marginBottom: '60px',
            maxWidth: '900px',
            margin: '0 auto 60px'
          }}>
            Complement your mental health billing with our full suite of <strong>healthcare revenue cycle management services</strong>. 
            <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}> MDeRCM</Link> offers end-to-end solutions powered by AI automation.
          </p>

          <div className="mental-services-grid">
            {[
              {
                icon: '💳',
                title: 'Payment Posting Services',
                desc: 'Automated payment posting with 99%+ accuracy for faster reconciliation and optimized cash flow.',
                link: '/healthcare/ai/payment-posting'
              },
              {
                icon: '📊',
                title: 'Accounts Receivable',
                desc: 'AI-powered AR automation reducing outstanding receivables by 60% and accelerating collections by 70%.',
                link: '/healthcare/ai/accounts-receivable'
              },
              {
                icon: '🎯',
                title: 'Denial Management',
                desc: 'Intelligent denial management with automated prevention, tracking, and appeals for maximum recovery.',
                link: '/healthcare/ai/denial-management'
              },
              {
                icon: '🔍',
                title: 'Compliance Management',
                desc: 'Automated compliance monitoring ensuring regulatory adherence, audit readiness, and risk reduction.',
                link: '/healthcare/ai/compliance-agent'
              },
              {
                icon: '💼',
                title: 'Prior Authorization',
                desc: 'Streamlined prior authorization process reducing delays and improving approval rates with AI automation.',
                link: '/healthcare/ai/prior-authorization'
              },
              {
                icon: '📞',
                title: 'Patient Intake',
                desc: 'Automated patient intake improving data accuracy, reducing wait times, and enhancing patient experience.',
                link: '/healthcare/ai/patient-intake'
              },
              {
                icon: '✓',
                title: 'Eligibility Verification',
                desc: 'Real-time insurance eligibility checking that reduces denials by 95% and streamlines patient intake.',
                link: '/healthcare/ai/eligibility-check'
              },
              {
                icon: '📄',
                title: 'Policy Verification',
                desc: 'Comprehensive policy status verification prevents coverage-related denials and billing errors.',
                link: '/healthcare/ai/policy-status-verification'
              }
            ].map((service, i) => (
              <article
                key={i}
                style={{
                  backgroundColor: '#ffffff',
                  padding: '30px',
                  borderRadius: '16px',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '15px', color: '#F26522' }}>{service.icon}</div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#001F2B', 
                  marginBottom: '12px' 
                }}>
                  {service.title}
                </h3>
                <p style={{ 
                  fontSize: '15px', 
                  color: '#555', 
                  lineHeight: '1.6',
                  marginBottom: '20px',
                  flex: 1
                }}>
                  {service.desc}
                </p>
                <Link 
                  to={service.link}
                  style={{ 
                    color: '#F26522', 
                    textDecoration: 'none', 
                    fontWeight: '600',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'gap 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.gap = '12px'}
                  onMouseOut={(e) => e.currentTarget.style.gap = '8px'}
                >
                  Learn More →
                </Link>
              </article>
            ))}
          </div>
        </div>

        {/* WHY CHOOSE MDERCM SECTION - 4x2 GRID */}
        <div style={{ 
          padding: '80px 5%', 
          backgroundColor: '#001F2B',
          color: '#ffffff',
          position: 'relative',
          zIndex: 10
        }}>
          <style>{`
            .mental-reasons-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 30px;
              max-width: 1400px;
              margin: 0 auto;
            }
            @media (max-width: 1200px) {
              .mental-reasons-grid {
                grid-template-columns: repeat(3, 1fr);
              }
            }
            @media (max-width: 900px) {
              .mental-reasons-grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }
            @media (max-width: 600px) {
              .mental-reasons-grid {
                grid-template-columns: 1fr;
              }
            }
          `}</style>

          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}>
            Why Choose MDeRCM for Mental Health Revenue Cycle Management?
          </h2>

          <p style={{ 
            textAlign: 'center', 
            fontSize: '17px', 
            color: '#dddddd', 
            marginBottom: '60px',
            maxWidth: '900px',
            margin: '0 auto 60px',
            lineHeight: '1.8'
          }}>
            As one of the <strong>best revenue cycle management companies</strong>, <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> provides industry-leading 
            <strong> revenue cycle management services USA</strong> with proven results and dedicated support for mental health providers nationwide.
          </p>

          <div className="mental-reasons-grid">
            {[
              {
                icon: '🏆',
                title: 'Behavioral Health Expertise',
                desc: 'Over 10+ years specializing in mental health revenue cycle management with deep understanding of authorization requirements and payer policies.'
              },
              {
                icon: '🤖',
                title: 'Advanced AI Technology',
                desc: 'Cutting-edge AI delivering 95% authorization approval rates and 99.7% documentation accuracy for mental health claims.'
              },
              {
                icon: '💵',
                title: 'Maximize Revenue',
                desc: 'Increase collections by up to 70% through optimized authorization workflows, faster processing, and proactive denial management.'
              },
              {
                icon: '⚡',
                title: '24/7 Support',
                desc: 'Round-the-clock dedicated support team with mental health billing expertise ensuring seamless operations.'
              },
              {
                icon: '🔒',
                title: 'HIPAA Compliant',
                desc: 'Fully compliant with HIPAA and mental health privacy regulations, ensuring data security and patient confidentiality.'
              },
              {
                icon: '📈',
                title: 'Scalable Solutions',
                desc: 'Flexible services that grow with your practice, from solo practitioners to large behavioral health organizations.'
              },
              {
                icon: '💡',
                title: 'Transparent Reporting',
                desc: 'Real-time dashboards and detailed analytics providing complete visibility into authorization status and revenue cycle performance.'
              },
              {
                icon: '🎯',
                title: 'Proven ROI',
                desc: 'Most mental health practices see positive ROI within 3-6 months with average cost reduction of 50% and revenue increase of 70%.'
              },
            ].map((reason, i) => (
              <article
                key={i}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  padding: '35px 25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>{reason.icon}</div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#ffffff', 
                  marginBottom: '12px' 
                }}>
                  {reason.title}
                </h3>
                <p style={{ 
                  fontSize: '15px', 
                  color: '#dddddd', 
                  lineHeight: '1.6'
                }}>
                  {reason.desc}
                </p>
              </article>
            ))}
          </div>

          <div style={{
            textAlign: 'center',
            marginTop: '60px'
          }}>
            <Link 
              to="/free-trial"
              style={{
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '18px 50px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '18px',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#d94d12';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#F26522';
                e.target.style.transform = 'scale(1)';
              }}
            >
              Start Your Free Trial Today
            </Link>
          </div>
        </div>

        <MENTALAutomationSection /> 
        <MDeRCMMentalComponent /> 
        <ConsultationCTA /> 
      </div>
    </>
  );
};

export default MentalHealth;